import { BasePlugin } from "../base-plugin";
import type {
  PluginCapabilities,
  IShell,
  PluginRegistrationContext,
} from "../interfaces";
import type {
  IEntityService,
  BaseEntity,
  EntityAdapter,
  EntityTypeConfig,
} from "@brains/entity-service";
import type {
  BatchOperation,
  EnqueueJobRequest,
  JobOptions,
} from "@brains/job-queue";
import type { ServicePluginContext } from "./context";
import { createServicePluginContext } from "./context";
import type { ApiRouteDefinition } from "../types/api-routes";
import type { WebRouteDefinition } from "../types/web-routes";

/**
 * Base class for service plugins
 * Service plugins extend core functionality with entity management, job queuing, and AI generation
 */
export abstract class ServicePlugin<TConfig, TConfigInput> extends BasePlugin<
  TConfig,
  TConfigInput,
  ServicePluginContext
> {
  public readonly type = "service" as const;

  /** Get API routes for this plugin (override in subclass) */
  getApiRoutes(): ApiRouteDefinition[] {
    return [];
  }

  /** Get web routes for this plugin (override in subclass) */
  getWebRoutes(): WebRouteDefinition[] {
    return [];
  }

  /**
   * Register the plugin with shell - creates ServicePluginContext internally
   */
  override async register(
    shell: IShell,
    registrationContext?: PluginRegistrationContext,
  ): Promise<PluginCapabilities> {
    // Create typed context from shell
    const context = createServicePluginContext(
      shell,
      this.id,
      registrationContext,
    );
    this.context = context;

    // Set up message handlers
    this.setupMessageHandlers(context);

    // Call lifecycle hook with typed context
    await this.onRegister(context);

    return this.getCapabilities();
  }

  /**
   * Get the entity service for direct access
   */
  protected get entityService(): IEntityService {
    const context = this.getContext();
    return context.entityService;
  }

  /**
   * Helper method to register an entity type
   */
  protected registerEntityType<T extends BaseEntity>(
    entityType: string,
    schema: EntityAdapter<T>["schema"],
    adapter: EntityAdapter<T>,
    config?: EntityTypeConfig,
  ): void {
    const context = this.getContext();
    context.entities.register(entityType, schema, adapter, config);
  }

  /**
   * Helper method to enqueue a job
   * Pass toolContext in the request for tool-routed jobs, or omit it for background jobs.
   */
  protected async enqueueJob(request: EnqueueJobRequest): Promise<string> {
    const context = this.getContext();
    return context.jobs.enqueue(request);
  }

  /**
   * Helper method to enqueue a batch of operations
   */
  protected async enqueueBatch(
    operations: BatchOperation[],
    options?: JobOptions,
  ): Promise<string> {
    const context = this.getContext();
    return context.jobs.enqueueBatch(operations, options);
  }

  /**
   * Override to register entity types during initialization
   */
  protected async registerEntityTypes(
    _context: ServicePluginContext,
  ): Promise<void> {
    // Default implementation does nothing
    // Override in subclasses to register entity types
  }

  /**
   * Override to register job handlers during initialization
   */
  protected async registerJobHandlers(
    _context: ServicePluginContext,
  ): Promise<void> {
    // Default implementation does nothing
    // Override in subclasses to register job handlers
  }

  /**
   * Override onRegister to add service-specific initialization
   */
  protected override async onRegister(
    context: ServicePluginContext,
  ): Promise<void> {
    // Register entity types first
    await this.registerEntityTypes(context);

    // Register job handlers
    await this.registerJobHandlers(context);
  }
}
